import React, { useContext, useState } from "react"
import { Typography, Button, CircularProgress, Box, Divider, Grid, useMediaQuery, Checkbox } from "@mui/material"
import BookOutlinedIcon from "@mui/icons-material/BookOutlined"
import EuroSymbolTwoToneIcon from "@mui/icons-material/EuroSymbolTwoTone"
import UserContext from "../../../context/UserContext"
import useMutation from "../../../hooks/useMutation"
import StateContext from "../../../context/StateContext"
import useFetch from "../../../hooks/useFetch"
import PotentialPartners from "./PotentialPartners"
import CancelDialog from "./CancelDialog"

const BookActions = ({ bookInLedger, book, isMobile, setCurrentTab }) => {
  const { ledgerRefetch } = useContext(StateContext)
  const { userData } = useContext(UserContext)
  const userId = userData.user.id

  const [finishCheckbox, setFinishCheckbox] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)

  const isVerySmallPhone = useMediaQuery("(max-height:700px)")

  const { data, loading: ledgerLoading, refetch: infoRefetch } = useFetch(`/ledger/book/${book?.id}`, { skip: !book })
  const { mutate: cancelMutation, loading: cancelLoading } = useMutation("/ledger", "DELETE")
  const { mutate: finishMutation, loading: finishLoading } = useMutation("/ledger/finish")
  const { mutate: buyOrSellMutation, loading: buyOrSellLoading } = useMutation("/ledger")

  const loading = ledgerLoading || cancelLoading || finishLoading || buyOrSellLoading

  const handleBuy = async () => {
    try {
      await buyOrSellMutation({
        userId,
        bookId: book.id,
        action: "buy",
      })

      // setSnackbarMsg("Buch zum Kauf eingestellt")
    } catch (e) {
      console.log(e.message)
    }
    setCurrentTab(2)
    ledgerRefetch()
  }

  const handleSell = async () => {
    try {
      await buyOrSellMutation({
        userId,
        bookId: book.id,
        action: "sell",
      })

      // setSnackbarMsg("Buch zum Verkauf eingestellt")
    } catch (e) {
      console.log(e.message)
    }
    setCurrentTab(2)
    ledgerRefetch()
  }

  const handleFinish = async () => {
    const preis = null
    try {
      await finishMutation({
        ...bookInLedger, // just for backend validation
        id: bookInLedger.id, // only these fields are really needed
        priceNegotiated: preis,
      })

      // setSnackbarMsg("Abgeschlossen!")
    } catch (e) {
      console.log(e)
    }

    setFinishCheckbox(false)
    ledgerRefetch()
  }

  const handleCancel = async () => {
    try {
      await cancelMutation({
        ...bookInLedger, // just for backend validation
        id: bookInLedger.id, // only these fields are really needed
      })

      // setSnackbarMsg(`${txt} abgebrochen`)
    } catch (e) {
      console.log(e)
    }

    setCancelDialogOpen(false)
    infoRefetch()
    ledgerRefetch()
  }

  // const isSearch = bookInLedger && bookInLedger.status === "search"
  const isMatched = bookInLedger && bookInLedger.status === "matched"
  const isFinished = bookInLedger && bookInLedger.status === "finished"

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
        <CircularProgress size={60} thickness={3} color="primary" />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        alignContent: "space-between",
        justifyContent: "center",
      }}>
      <Grid container sx={{ p: 1 }} spacing={isVerySmallPhone ? 2 : 4} direction={isMobile ? "column" : "row"}>
        <Grid item xs={6}>
          <Button
            disabled={bookInLedger}
            size={isMobile ? "small" : "large"}
            fullWidth
            variant="contained"
            onClick={handleBuy}
            sx={{ mb: isMobile ? 0 : 1 }}
            endIcon={<BookOutlinedIcon fontSize="small" />}>
            Buch kaufen
          </Button>
          {/* <Typography variant={isMobile ? "caption" : "body2"} sx={{}}>
            Es wird ein Verkäufer gesucht, der das Buch verkaufen möchte.
          </Typography> */}
          {!bookInLedger && <PotentialPartners countPotentialPartners={data.countSellers} action="buy" isMobile={isMobile} />}
        </Grid>

        <Grid item xs={6}>
          <Button
            disabled={bookInLedger}
            size={isMobile ? "small" : "large"}
            fullWidth
            variant="contained"
            onClick={handleSell}
            sx={{ mb: isMobile ? 0 : 1 }}
            endIcon={<EuroSymbolTwoToneIcon fontSize="small" />}>
            Buch verkaufen
          </Button>
          {/* <Typography variant={isMobile ? "caption" : "body2"} sx={{}}>
            Es wird ein Käufer gesucht, der das Buch kaufen möchte.
          </Typography> */}
          {!bookInLedger && <PotentialPartners countPotentialPartners={data.countBuyers} action="sell" isMobile={isMobile} />}
        </Grid>
      </Grid>

      {!bookInLedger && (
        <Typography sx={{ color: "gray", p: 1 }} variant="caption">
          <Divider />
          {isVerySmallPhone
            ? "Es wird nur ein Partner gesucht. Kein Kauf garantiert. Preis nach Absprache."
            : "Es wird lediglich ein Partner gesucht und ein engültiger Kauf / Verkauf ist nicht garantiert. Preis nach Absprache mit dem Partner."}
        </Typography>
      )}

      {bookInLedger && (
        <Grid container sx={{ p: 1 }} spacing={isVerySmallPhone ? 2 : 4} direction="row">
          <Grid item xs={12}>
            <Divider sx={{ width: 1 }} />
            <Button
              sx={{ my: 1 }}
              fullWidth
              disabled={!isMatched || !finishCheckbox}
              size={isMobile ? "small" : "medium"}
              variant="contained"
              onClick={handleFinish}>
              {bookInLedger.action === "buy" ? "Kauf" : "Verkauf"} abschließen
            </Button>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
              <Checkbox disabled={!isMatched} sx={{ p: 0, mr: 0.5 }} value={finishCheckbox} onChange={() => setFinishCheckbox((v) => !v)} />
              <Typography variant="body2" sx={{ color: isMatched ? null : "#999" }}>
                <i>
                  {bookInLedger.action === "buy"
                    ? "Ich bestätige, dass ich das Buch erhalten und dem Verkäufer den ausgemachten Preis bezahlt habe."
                    : "Ich bestätige, dass ich von dem Käufer den ausgemachten Preis erhalten und das Buch übergeben habe."}
                </i>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              disabled={isFinished}
              size={isMobile ? "small" : "medium"}
              variant="contained"
              onClick={() => setCancelDialogOpen(true)}
              sx={{ backgroundColor: "salmon", "&:hover": { backgroundColor: "red" } }}>
              {bookInLedger.action === "buy" ? "Kauf" : "Verkauf"} abbrechen
            </Button>
          </Grid>
        </Grid>
      )}

      {cancelDialogOpen && (
        <CancelDialog onClose={() => setCancelDialogOpen(false)} onConfirm={handleCancel} action={bookInLedger.action} bookTitle={book.title} />
      )}
    </Box>
  )
}

export default BookActions
